import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { User } from '../models/User';
import { UserService } from '../services/UserService';
import { logger } from '../utils/logger';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (uid: string, data: Partial<User>) => Promise<void>;
  refreshCurrentUser: () => Promise<void>;
  waitForUser: (timeout?: number) => Promise<User | null>; // Wait for user to be loaded after auth operations
  /** Send a password reset email via Supabase; redirects back to /reset-password */
  resetPassword: (email: string) => Promise<void>;
  /** Update the current user's password (called from the reset-password page after recovery link is followed) */
  updatePassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const lastFetchedUserIdRef = useRef<string | null>(null); // Track last fetched user to avoid duplicate calls
  const currentUserRef = useRef<User | null>(null); // Track current user for comparison without causing re-renders
  // Two triggers can both call handleSession for the same user nearly
  // simultaneously (the initial getSession() resolution and an
  // onAuthStateChange 'SIGNED_IN' event; React StrictMode's dev-only double
  // effect invocation makes this more likely, but it isn't StrictMode-only).
  // Without de-duplication both proceed concurrently into
  // UserService.getOrCreateProfile, occasionally surfacing as a transient
  // "Error loading user profile" that clears itself on reload. Track the
  // in-flight fetch so a second concurrent call reuses it instead of racing.
  const inFlightRef = useRef<{ userId: string; promise: Promise<void> } | null>(null);

  const handleSession = useCallback(async (session: Session | null) => {
    if (!session?.user) {
      logger.info('[AuthProvider] Clearing session - no user');
      setCurrentUser(null);
      currentUserRef.current = null;
      setLoading(false);
      lastFetchedUserIdRef.current = null;
      return;
    }

    const userId = session.user.id;
    logger.info('[AuthProvider] handleSession called for user:', userId);

    // Skip if we already have this user and they're already loaded
    if (lastFetchedUserIdRef.current === userId && currentUserRef.current?.uid === userId) {
      logger.info('[AuthProvider] User already loaded, skipping fetch');
      return;
    }

    // A fetch for this exact user is already in flight (e.g. the initial
    // getSession() resolution and an onAuthStateChange event both firing for
    // the same sign-in) -- reuse it instead of starting a second, racing
    // getOrCreateProfile call.
    if (inFlightRef.current?.userId === userId) {
      logger.info('[AuthProvider] Fetch already in flight for user, awaiting it:', userId);
      await inFlightRef.current.promise;
      return;
    }

    setLoading(true);

    const fetchPromise = (async () => {
      try {
        logger.info('[AuthProvider] Fetching user profile...');
        const profile = await UserService.getOrCreateProfile(session.user);
        logger.info('[AuthProvider] User profile loaded:', profile.uid);
        lastFetchedUserIdRef.current = userId;

        // Only update if the user data actually changed to prevent unnecessary re-renders
        setCurrentUser(prevUser => {
          // If user ID changed, always update
          if (!prevUser || prevUser.uid !== profile.uid) {
            currentUserRef.current = profile;
            return profile;
          }
          // If user ID is the same, only update if data actually changed
          const dataChanged =
            prevUser.email !== profile.email ||
            prevUser.firstName !== profile.firstName ||
            prevUser.lastName !== profile.lastName ||
            prevUser.orgId !== profile.orgId ||
            prevUser.role !== profile.role;

          if (dataChanged) {
            currentUserRef.current = profile;
            return profile;
          }
          return prevUser;
        });
      } catch (error) {
        logger.error('[AuthProvider] Error loading user profile:', error);
        setCurrentUser(null);
        currentUserRef.current = null;
        lastFetchedUserIdRef.current = null;
      } finally {
        setLoading(false);
        if (inFlightRef.current?.userId === userId) {
          inFlightRef.current = null;
        }
      }
    })();

    inFlightRef.current = { userId, promise: fetchPromise };
    await fetchPromise;
  }, []);

  useEffect(() => {
    let isMounted = true;
    let subscription: { unsubscribe: () => void } | null = null;

    const init = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (!error && isMounted) {
        await handleSession(session);
      }

      const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
        // Only handle session changes for meaningful events
        // Ignore TOKEN_REFRESHED and INITIAL_SESSION events as they don't indicate actual user data changes
        if (event === 'TOKEN_REFRESHED') {
          return;
        }
        // For SIGNED_IN and USER_UPDATED events, handle the session
        // For SIGNED_OUT, handle null session
        if (event === 'SIGNED_OUT' || event === 'SIGNED_IN' || event === 'USER_UPDATED') {
          handleSession(session);
        }
      });

      subscription = authListener.subscription;
    };

    init();

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, [handleSession]);

  const signIn = async (email: string, password: string) => {
    try {
      logger.info('[AuthProvider] Starting sign in for:', email);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        logger.error('[AuthProvider] Supabase auth error:', error);
        throw error;
      }

      // Verify session was returned
      if (!data?.session) {
        logger.error('[AuthProvider] No session returned from signInWithPassword');
        throw new Error('Authentication failed: No session returned. Please try again.');
      }

      logger.info('[AuthProvider] Session received, handling session...');
      await handleSession(data.session);
      logger.info('[AuthProvider] Sign in successful');
    } catch (error) {
      logger.error('[AuthProvider] Error signing in:', error);
      throw error;
    }
  };

  const signup = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) {
        throw error;
      }
      // Use the returned session directly instead of waiting for onAuthStateChange
      if (data?.session) {
        await handleSession(data.session);
      }
    } catch (error) {
      logger.error('Error signing up:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    // Supabase sends an email containing a magic link that lands on
    // `${origin}/reset-password#access_token=...&type=recovery`. The auth
    // listener picks up the recovery token automatically; the ResetPassword
    // page then prompts for a new password.
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) {
      logger.error('Error requesting password reset:', error);
      throw error;
    }
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      logger.error('Error updating password:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      setCurrentUser(null);
      setLoading(true);

      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
    } catch (error) {
      logger.error('Error signing out:', error);
      setLoading(false);
      throw error;
    }
  };

  const updateUser = async (uid: string, data: Partial<User>) => {
    try {
      const { data: authState } = await supabase.auth.getUser();
      if (!authState.user) {
        throw new Error('Not authenticated');
      }

      // Merge patch with currentUser so callers that send a partial payload
      // don't accidentally clobber fields they didn't intend to change.
      const merged: Partial<User> = {
        firstName: data.firstName !== undefined ? data.firstName : currentUser?.firstName,
        lastName: data.lastName !== undefined ? data.lastName : currentUser?.lastName,
        phoneNumber: data.phoneNumber !== undefined ? data.phoneNumber : currentUser?.phoneNumber,
        address: data.address !== undefined ? data.address : currentUser?.address,
        role: data.role !== undefined ? data.role : currentUser?.role,
        orgId: data.orgId !== undefined ? data.orgId : currentUser?.orgId,
      };

      await UserService.updateUser(uid, merged);
      const refreshed = await UserService.getCurrentUser(uid);
      if (refreshed) {
        setCurrentUser(refreshed);
        currentUserRef.current = refreshed;
      }
    } catch (error) {
      logger.error('Error updating user:', error);
      throw error;
    }
  };

  const refreshCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setCurrentUser(null);
      return;
    }

    try {
      const profile = await UserService.getOrCreateProfile(user);
      setCurrentUser(profile);
    } catch (error) {
      logger.error('[AuthProvider] Error refreshing user:', error);
    }
  };

  /**
   * Wait for the current user to be loaded (useful after signIn/signUp)
   * @param timeout Maximum time to wait in milliseconds (default: 5000ms)
   * @returns Promise that resolves with the user or null if timeout/not found
   */
  const waitForUser = async (timeout: number = 5000): Promise<User | null> => {
    // If user is already loaded and not loading, return immediately
    if (currentUser && !loading) {
      return currentUser;
    }

    return new Promise((resolve) => {
      const startTime = Date.now();
      
      const checkUser = () => {
        // If user is loaded, resolve
        if (currentUser && !loading) {
          resolve(currentUser);
          return;
        }

        // If timeout exceeded, resolve with null
        if (Date.now() - startTime > timeout) {
          logger.warn('[AuthProvider] waitForUser timeout exceeded');
          resolve(null);
          return;
        }

        // Check again after a short delay
        setTimeout(checkUser, 100);
      };

      checkUser();
    });
  };

  const value = {
    currentUser,
    loading,
    signIn,
    signup,
    logout,
    updateUser,
    refreshCurrentUser,
    waitForUser,
    resetPassword,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 